// import components
import { GridRowModes } from '@mui/x-data-grid-pro';
import type { DataGridAction } from 'components/shared/DataGrid/DataGrid.types';

// import hooks
import { useCallback } from 'react';

// import types
import type {
  CustomDutyLine,
  DraftDutyLine
} from 'RestAPIs/employees/hours-table/types';
import type { RowActionsParams } from './useRowActions.types';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// import utils
import dayjs from 'dayjs';
import {
  isDraft,
  isRowBlocked,
  isRowCredited,
  isRowCrediting,
  isRowNominal,
  isRowRealized,
  resetDraft
} from '../HoursTable.utils';
import showSnackbar from 'components/shared/Snackbar';

export function useRowActions({
  t,
  tRoot,
  rowModesModel,
  copyMode,
  copySourceRowId,
  pastedRows,
  onCancelRowEditAction,
  onFinishRowEditAction,
  onPaste,
  onUndo,
  onResetCopy,
  onFinishCopy,
  onCopy,
  onAddRow,
  onClearDraft,
  onRemoveDraft,
  onBlockToggle,
  onCreditingToggle,
  onDutyInformation,
  onNominalHoursToggle,
  apiRef,
  setProcessedRows,
  clearRow,
  isEmployeePortal = false,
  hasEmployeePortalCreateDuty = false,
  hasEmployeePortalDutyTimeEditable = false,
  hasEmployeePortalDutyTimeEditDht = false,
  setRowModesModel,
  setEditingRowId,
  setIsRowEditModeActive
}: RowActionsParams) {
  const getMissingRequiredFields = (row: any) => {
    return [
      !row.dvb ? 'dvb' : null,
      !row.organizationalUnit && !row.isNominalRow
        ? 'organizationalUnit'
        : null,
      !row.dutyTime && row.isNominalRow ? 'dutyTime' : null,
      (!row.periods || row.periods.length === 0) && !row.isNominalRow
        ? 'periods'
        : null,
      (!row.dutyType || row.dutyType === '') && row.isNominalRow
        ? 'dutyType'
        : null
    ].filter(Boolean);
  };

  return useCallback(
    (id: string | number, row?: CustomDutyLine | DraftDutyLine) => {
      if (!row) return [];
      const hasOrgUnitAndDvb = (row) =>
        row.organizationalUnit != undefined && row.dvb != undefined;
      const invalidDateString = (date: string) =>
        dayjs(date).isValid() === false;

      /* ───────────────── edit-mode actions ───────────────── */
      if (rowModesModel[id]?.mode === GridRowModes.Edit) {
        return [
          {
            tooltipTitle: tRoot('cancel'),
            icon: 'xmark' as IconType,
            onClick: () => {
              onCancelRowEditAction(row);
              apiRef.current?.stopRowEditMode({
                id,
                ignoreModifications: true
              });
            }
          },
          {
            tooltipTitle: tRoot('save'),
            icon: 'check' as IconType,
            onClick: () => {
              const updatedRow = apiRef.current?.getRowWithUpdatedValues(id);
              const missing = getMissingRequiredFields(updatedRow);

              if (missing.length) {
                showSnackbar({
                  severity: 'error',
                  message: t('validation.requiredFieldsMissing')
                });
                return; // blokkeer save
              }

              onFinishRowEditAction();
              apiRef.current?.stopRowEditMode({ id });
            }
          }
        ];
      }

      /* ───────────────── copy / paste mode ───────────────── */
      if (copyMode) {
        // source-row buttons
        if (copySourceRowId === row.id) {
          return [
            {
              tooltipTitle: t('cancelCopy'),
              icon: 'xmark' as IconType,
              onClick: onResetCopy
            },
            {
              tooltipTitle: t('finishCopy'),
              icon: 'check' as IconType,
              onClick: onFinishCopy
            }
          ];
        }

        // row already pasted → show eraser
        if (pastedRows.has(row.id)) {
          return [
            {
              tooltipTitle: t('undoPaste'),
              icon: 'eraser' as IconType,
              onClick: () => onUndo(row as CustomDutyLine)
            }
          ];
        }

        // normal rows → show paste
        return [
          ...(isRowRealized(row) || isRowCrediting(row) || isRowNominal(row)
            ? []
            : [
                {
                  tooltipTitle: t('paste'),
                  icon: 'paste' as IconType,
                  onClick: () => onPaste(row as CustomDutyLine)
                },
                ...(row?.requestId && !isRowCrediting(row)
                  ? [
                      {
                        tooltipTitle: t('deleteRow'),
                        icon: 'trash-can' as IconType,
                        onClick: () => {
                          clearRow(row as CustomDutyLine);

                          setProcessedRows &&
                            setProcessedRows((prev) => {
                              const next = { ...prev };
                              row.requestId &&
                                next.deletedRows.add(row.requestId);
                              next.createdRows.delete(row.id);
                              next.updatedRows.delete(row.id);
                              return next;
                            });
                        }
                      }
                    ]
                  : [])
              ])
        ];
      }

      /* ───────────────── default row actions ───────────────── */
      const emptyRow = isDraft(row);
      const rowIsEdited =
        emptyRow &&
        Object.keys(row).some((key) => row[key] !== resetDraft(row)[key]);

      const clearAction = rowIsEdited &&
        !!row.requestId && {
          tooltipTitle: t('clearRow'),
          icon: 'eraser' as IconType,
          onClick: () => onClearDraft(row),
          sx: {
            opacity: 0,
            transition: 'opacity .2s',
            '.MuiDataGrid-row:hover &': { opacity: 1 }
          }
        };

      const plusAction = {
        tooltipTitle: t('addNewRow'),
        icon: 'circle-plus' as IconType,
        onClick: () => onAddRow(row),
        sx: {
          opacity: 0,
          transition: 'opacity .2s',
          '.MuiDataGrid-row:hover &': { opacity: 1 }
        }
      };

      const canUseCreateDutyActions =
        !isEmployeePortal || hasEmployeePortalCreateDuty;
      const canEditEmployeePortalRow = isEmployeePortal
        ? emptyRow
          ? hasEmployeePortalCreateDuty
          : hasEmployeePortalDutyTimeEditable ||
            hasEmployeePortalDutyTimeEditDht
        : false;
      const editFieldToFocus = emptyRow
        ? 'dvb'
        : hasEmployeePortalDutyTimeEditable
        ? 'periods'
        : 'dutyType';

      const copyAction = canUseCreateDutyActions &&
        !isRowCrediting(row) &&
        !isRowNominal(row) && {
          tooltipTitle: tRoot('copy'),
          icon: 'copy' as IconType,
          onClick: () => onCopy(row as CustomDutyLine)
        };

      const dutyInformationAction = {
        tooltipTitle: t('dutyInformation.title'),
        icon: 'eye' as IconType,
        onClick: () => onDutyInformation(row as CustomDutyLine)
      };

      const creditingAction = !isEmployeePortal &&
        isRowRealized(row) &&
        !isRowCredited(row) && {
          tooltipTitle: t('crediting'),
          icon: 'receipt' as IconType,
          onClick: () => onCreditingToggle(row as CustomDutyLine)
        };

      const undoCreditingAction = !isEmployeePortal &&
        !isRowRealized(row) &&
        isRowCrediting(row) && {
          tooltipTitle: t('undoCrediting'),
          icon: 'arrows-rotate' as IconType,
          onClick: () => onCreditingToggle(row as CustomDutyLine)
        };

      const blockAction = !isEmployeePortal &&
        !isRowRealized(row) &&
        !isRowCredited(row) &&
        !isRowCrediting(row) && {
          tooltipTitle: isRowBlocked(row) ? t('unblock') : t('block'),
          icon: isRowBlocked(row)
            ? ('lock' as IconType)
            : ('lock-open' as IconType),
          onClick: () => onBlockToggle(row as CustomDutyLine)
        };

      const editAction = canEditEmployeePortalRow && {
        tooltipTitle: tRoot('edit'),
        icon: 'pen' as IconType,
        onClick: () => {
          setRowModesModel?.((model) => ({
            ...model,
            [row.id]: {
              mode: GridRowModes.Edit,
              fieldToFocus: editFieldToFocus
            }
          }));
          setEditingRowId?.(row.id);
          setIsRowEditModeActive?.(true);
        }
      };

      const nominalAction = {
        tooltipTitle: t('addNominalHours'),
        icon: 'clock' as IconType,
        onClick: () => onNominalHoursToggle(row as CustomDutyLine),
        sx: {
          opacity: 0,
          transition: 'opacity .2s',
          '.MuiDataGrid-row:hover &': { opacity: 1 }
        }
      };

      const removeAction = emptyRow && {
        tooltipTitle: t('removeRow'),
        icon: 'trash-can' as IconType,
        onClick: () => onRemoveDraft(row),
        sx: {
          opacity: 0,
          transition: 'opacity .2s',
          '.MuiDataGrid-row:hover &': { opacity: 1 }
        }
      };

      let actions: DataGridAction[] = [];

      // rows without orgUnit+dvb only show local helpers
      if (!hasOrgUnitAndDvb(row) && !isRowNominal(row)) {
        !isEmployeePortal && actions.push(nominalAction);
        canUseCreateDutyActions && actions.push(plusAction);
        if (removeAction && invalidDateString(row?.id))
          actions.push(removeAction);
        if (clearAction) actions.push(clearAction);
        return actions;
      }

      // full-blown row
      actions.unshift(
        ...(creditingAction ? [creditingAction] : []),
        ...(undoCreditingAction ? [undoCreditingAction] : []),
        ...(blockAction ? [blockAction] : []),
        ...(editAction ? [editAction] : []),
        dutyInformationAction,
        ...(copyAction ? [copyAction] : []),
        ...(canUseCreateDutyActions ? [plusAction] : [])
      );

      if (removeAction) actions.push(removeAction);
      return actions;
    },
    [
      t,
      tRoot,
      rowModesModel,
      copyMode,
      copySourceRowId,
      pastedRows,
      onCancelRowEditAction,
      onPaste,
      onUndo,
      onResetCopy,
      onFinishCopy,
      onCopy,
      onAddRow,
      onClearDraft,
      onRemoveDraft,
      onBlockToggle,
      onDutyInformation,
      onNominalHoursToggle,
      apiRef,
      isEmployeePortal,
      hasEmployeePortalCreateDuty,
      hasEmployeePortalDutyTimeEditable,
      hasEmployeePortalDutyTimeEditDht,
      setRowModesModel,
      setEditingRowId,
      setIsRowEditModeActive
    ]
  );
}
